import { useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { onAuthExpired } from '../../lib/authEvents'
import { queryClient } from '../../lib/queryClient'
import { buildReturnToRedirect } from '../../lib/returnTo'
import { useAuthStore } from '../../stores/authStore'
import { useResearchStatusStore } from '../../stores/chat/researchStatusStore'

const COOLDOWN_MS = 2000

/**
 * Subscribes to auth-expired events and orchestrates the global response:
 * cancels in-flight queries, clears query cache, resets polling, flips auth
 * state, shows a single toast, and redirects with ?returnTo= for post-login
 * resume. The 2s cooldown collapses concurrent 401 bursts to one user-visible
 * action.
 */
export function AuthExpiryListener(): null {
  const navigate = useNavigate()
  const location = useLocation()
  const locationRef = useRef(location)

  // Keep the latest location available to the long-lived subscriber without
  // re-subscribing on every navigation (which would also reset lastFiredAt
  // and defeat the cooldown).
  useEffect(() => {
    locationRef.current = location
  }, [location])

  // Subscribe once for the lifetime of the listener. `navigate` from
  // react-router is stable, and the latest location is read via locationRef
  // so we deliberately do not include `location` here.
  useEffect(() => {
    let lastFiredAt = 0
    const unsubscribe = onAuthExpired(async () => {
      const now = Date.now()
      if (now - lastFiredAt < COOLDOWN_MS) return
      lastFiredAt = now

      // Cancel before clear so in-flight queries cannot rehydrate the cache
      // after we've cleared it.
      await queryClient.cancelQueries()
      queryClient.clear()
      useResearchStatusStore.getState().reset()
      await useAuthStore.getState().logout()
      toast.error('Your session expired. Please log in again.')

      const current = locationRef.current
      const currentPath = current.pathname + current.search
      const target = buildReturnToRedirect(currentPath, '/auth')
      navigate(target, { replace: true })
    })
    return unsubscribe
  }, [navigate])

  return null
}
